/**
 * Meal Repetition Service
 * 
 * Pure functions for handling meal repetition logic including overlap detection,
 * meal cloning, leftover generation, and conflict resolution.
 */

import { v4 as uuidv4 } from 'uuid';
import { getParticipantsForMeal, getPrimaryMeal } from '@utils/Meals';

/**
 * Generate a short meal ID (8 characters)
 */
export function generateMealId() {
    return uuidv4().substring(0, 8);
}

/**
 * Create repeated meals for selected day/meal type combinations
 */
export function generateRepeatedMeals(sourceMeals, selections, profile, recipes) {
    const repeatedMeals = [];
    const ogLeftoverOffsets = getOriginalLeftoverOffsets(sourceMeals);

    selections.forEach(({ offset, mealType }) => {
        // Clone source meals for this selection
        const clonedMeals = sourceMeals.map(meal => ({
            ...meal,
            id: generateMealId(),
            meal: mealType,
            offset: offset
        }));

        repeatedMeals.push({
            mealType,
            offset,
            sourceMeals: clonedMeals
        });

        // Generate leftovers if the primary meal is fresh
        const { primary, recipe } = getPrimaryMeal(clonedMeals, recipes);
        if (primary?.meal_type === 'fresh' && recipe) {
            const participants = getParticipantsForMeal(clonedMeals, primary, profile);
            const neededPerMeal = participants.reduce((total, member) => total + (member.portion || 1), 0);

            ogLeftoverOffsets.forEach((dummy, i) => {
                const leftoverOffset = offset + i + 1;
                
                const sourceLeftovers = clonedMeals.map(sourceMeal => ({
                    ...sourceMeal,
                    id: generateMealId(),
                    meal_type: 'leftover',
                    parent: sourceMeal.id,
                    leftover_servings: neededPerMeal,
                    offset: leftoverOffset
                }));

                repeatedMeals.push({
                    mealType,
                    offset: leftoverOffset,
                    sourceMeals: sourceLeftovers
                });
            });
        }
    });

    return repeatedMeals;
}

/**
 * Get original leftover offsets from source meals
 */
function getOriginalLeftoverOffsets(sourceMeals) {
    const idsToRepeat = sourceMeals.map(m => m.id);
    // This would need access to the full plan to get leftovers
    // For now, return empty array - this logic needs plan context
    return [];
}

/**
 * Detect overlaps between repeated meals and existing plan
 */
export function detectOverlaps(repeatedMeals, existingPlan, idsToRepeat = []) {
    const overlaps = [];
    const clears = [];

    repeatedMeals.forEach(({ mealType, offset, sourceMeals }) => {
        // Find existing meals at this offset/meal type that aren't part of the original meal being repeated
        const isDestMeal = m => (
            !m.deleted && 
            m.meal === mealType && 
            m.offset === offset && 
            !idsToRepeat.includes(m.id) &&
            !idsToRepeat.includes(m.parent)
        );
        
        const destMeals = (existingPlan.items || []).filter(isDestMeal);

        if (destMeals.length > 0) {
            overlaps.push({
                mealType,
                offset,
                destMeals,
                sourceMeals
            });
        } else {
            clears.push({
                mealType,
                offset,
                sourceMeals
            });
        }
    });

    return { overlaps, clears };
}

/**
 * Check if a meal type/offset is a leftover from a selection
 */
export function isSelectionLeftover(offset, mealType, selections, ogLeftoverOffsets) {
    if (!ogLeftoverOffsets || ogLeftoverOffsets.length === 0) {
        return false;
    }

    return selections.some(sel => {
        return sel.mealType === mealType && 
               sel.offset < offset && 
               sel.offset + ogLeftoverOffsets.length >= offset;
    });
}

/**
 * Validate that selections don't conflict with original meal or its leftovers
 */
export function validateSelections(selections, ogOffset, ogMealType, ogLeftoverOffsets = []) {
    const errors = [];

    selections.forEach(({ offset, mealType }) => {
        // Can't select the original meal
        if (offset === ogOffset && mealType === ogMealType) {
            errors.push(`Cannot repeat meal to its original location (Day ${offset + 1} ${mealType})`);
        }

        // Can't select leftover slots
        if (isSelectionLeftover(offset, mealType, selections, ogLeftoverOffsets)) {
            errors.push(`Cannot select leftover slot (Day ${offset + 1} ${mealType})`);
        }
    });

    return errors;
}

/**
 * Process clears (non-conflicting meals) by applying them to the plan
 */
export function processClears(clears) {
    const dirtyMeals = [];

    clears.forEach(({ sourceMeals, mealType, offset }) => {
        sourceMeals.forEach(meal => {
            meal.meal = mealType;
            meal.offset = offset;

            if (!dirtyMeals.includes(meal)) {
                dirtyMeals.push(meal);
            }
        });
    });

    return dirtyMeals;
}

/**
 * Create a range of numbers from min to max (inclusive)
 */
export function createRange(min, max) {
    const arr = [];
    for (let i = min; i <= max; i++) {
        arr.push(i);
    }
    return arr;
}

/**
 * Resolution strategies for handling overlaps
 */
export const RepetitionStrategy = {
    REPLACE: 'replace',
    COMBINE: 'combine',
    CANCEL: 'cancel'
};

/**
 * Apply overlap resolution strategy
 */
export function applyResolutionStrategy(overlaps, clears, strategy) {
    switch (strategy) {
        case RepetitionStrategy.REPLACE:
            // Add overlaps to clears (they will replace existing meals)
            return [...clears, ...overlaps];
            
        case RepetitionStrategy.COMBINE:
            // Combine overlaps with existing meals
            return [...clears, ...overlaps.map(overlap => ({
                ...overlap,
                strategy: 'combine'
            }))];
            
        case RepetitionStrategy.CANCEL:
            // Only process non-conflicting clears
            return clears;
            
        default:
            throw new Error(`Unknown repetition strategy: ${strategy}`);
    }
}